import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ProfileContent,
  ButtonsWrapper,
  ProfileWrapper
} from '../../styledComponents'
import { useEffect, useRef, useState } from 'react'
import useResident, { convertFormTypeToResidentBody } from 'hooks/useResident'
import { Spinner } from 'components/generic'
import useHomeType from 'hooks/useHomeType'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { DnacprFormCard } from './FormCards/Dnacpr'
import { LpsFormCard } from './FormCards/Lps'
import { AllergiesFormCard } from './FormCards/Allergies'
import { GeneralInfoFormCard } from './FormCards/GeneralInfo'
import { FundingSourceFormCard } from './FormCards/FundingSource'
import { MedicationsFormCard } from './FormCards/Medications'
import { ReasonableAdjustmentsFormCard } from './FormCards/ReasonableAdjustments'
import dayjs from 'dayjs'
import { getNominative, showToast } from 'common/utils'
import { Home, ResidentBody, ResidentItem, UserRole } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import {
  cleanNHSFormattedNumber,
  getNHSFormattedNumber,
  handleOnFinishFailed,
  scrollToTop
} from 'screens/Residents/screens/ResidentProfileScreen/utils/utils'
import { generalConstants } from 'locale/general'
import OtherIdentifierCard from './FormCards/OtherIdentifier'
import { RcFile } from 'antd/lib/upload'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useRoleAccess } from 'components/AccessControl'
import { MentalHealthActFormCard } from './FormCards/MentalHealthAct'

type FormType = {
  dnacprReviewDate?: dayjs.Dayjs | null
  dnacprForm?: string | null
  admissionDate?: dayjs.Dayjs
  dolsStartDate?: dayjs.Dayjs
  dolsEndDate?: dayjs.Dayjs
  dolsReviewDate?: dayjs.Dayjs
  dolsApplicationDate?: dayjs.Dayjs
  nhsNumber?: string
} & Pick<
  ResidentBody,
  | 'residentAllergies'
  | 'otherIdentifiers'
  | 'dolsStatus'
  | 'dnacprStartDate'
  | 'mhaStatus'
  | 'mhaType'
  | 'dnacprDecisionMadeDate'
  | 'reasonableAdjustments'
>

export type ResidentCareInformationFormType = FormType

const ResidentCareInformation = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { id } = useParams()
  const [isDirty, setIsDirty] = useState(false)
  const [selectedFile, setSelectedFile] = useState<RcFile>()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const { hasRota } = useHomeType()

  const flags = useFeatureFlags({
    flagNames: ['reasonable_adjustments']
  })?.data
  const reasonableAdjustmentsEnabled = flags?.reasonable_adjustments?.enabled

  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident, updateResidentFormData } =
    useResident({
      id,
      onUpdateSuccess: () => {
        showToast({
          title: t(supConstants.RESIDENT_UPDATED, {
            variable: residentNominative
          })
        })
        navigate(`../profile`, { relative: 'path' })
      }
    })
  const { isPro, isEmar, isDoLSOverride, isFromCanada } = useHomeType()

  const convertFormTypeToPartialResidentBody = (
    values: FormType
  ): Partial<ResidentBody> => ({
    ...values,
    dnacprReviewDate: values?.dnacprReviewDate?.toISOString() || null,
    admissionDate: values?.admissionDate?.toISOString(),
    dolsApplicationDate: values?.dolsApplicationDate?.toISOString() || '',
    dolsReviewDate: values?.dolsReviewDate?.toISOString() || '',
    dolsStartDate: values?.dolsStartDate?.toISOString() || '',
    dolsEndDate: values?.dolsEndDate?.endOf('day').toISOString() || '',
    nhsNumber: cleanNHSFormattedNumber(values?.nhsNumber),
    dnacprForm: values?.dnacprForm ? undefined : null,
    mhaType: values.mhaStatus ? values.mhaType : ''
  })
  const handleOnFinish = async (values: FormType) => {
    setIsUpdateLoading(true)
    const formData = new FormData()
    if (selectedFile) {
      formData.append('dnacprFile', selectedFile, selectedFile.name)
    }
    try {
      const convertedValues = convertFormTypeToResidentBody(values, (vals) =>
        convertFormTypeToPartialResidentBody(vals)
      )
      setIsDirty(false)
      await updateResident(convertedValues)
      if (selectedFile) {
        await updateResidentFormData(formData)
      }
      setIsUpdateLoading(false)
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
      setIsUpdateLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (values: FormType) => {
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  useEffect(() => {
    if (!resident) {
      return
    }

    const updatedInitialValues = getInitialValues(resident, isFromCanada)
    form.setFieldsValue(updatedInitialValues)
    // we use ID as dependencies instead of resident
    // if we use resident, when we focus on window, the resident will refetch, and it will change
  }, [resident?.id])

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-care-info-loading` : 'care-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-care-info-no-resident` : 'care-info-no-resident'
        }
      />
    )
  }

  const initialValues: Partial<FormType> = getInitialValues(
    resident,
    isFromCanada
  )

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={testId ? `${testId}-care-info-wrapper` : 'care-info-wrapper'}
    >
      <PageHeader
        sticky
        title={t(supConstants.CARE_INFORMATION)}
        data-testid={testId ? `${testId}-care-info-header` : 'care-info-header'}
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-care-info-buttons` : 'care-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId ? `${testId}-care-info-cancel-btn` : 'care-info-cancel-btn'
            }
          >
            {t(supConstants.CANCEL)}
          </Button>
          <Button
            data-cy="submit-btn"
            disabled={isUpdateLoading}
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId ? `${testId}-care-info-save-btn` : 'care-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-care-info-content` : 'care-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form<FormType>
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={testId ? `${testId}-care-info-form` : 'care-info-form'}
        >
          {isPro && (
            <>
              <DnacprFormCard
                form={form}
                dnacprValue={resident.dnacpr}
                dnacprFormUrl={resident.dnacprForm}
                setSelectedFile={setSelectedFile}
              />
              <LpsFormCard
                form={form}
                dols={resident.dols || false}
                isDoLSOverride={isDoLSOverride}
              />
              <MentalHealthActFormCard form={form} />
              <AllergiesFormCard form={form} />
              {reasonableAdjustmentsEnabled && (
                <ReasonableAdjustmentsFormCard form={form} />
              )}
              <GeneralInfoFormCard
                resident={resident}
                isFromCanada={isFromCanada}
              />
              {(!hasRota || !isPro) && <FundingSourceFormCard />}
            </>
          )}
          {isPro && <OtherIdentifierCard />}
          {isEmar && <MedicationsFormCard />}
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

const getInitialValues = (
  resident: ResidentItem,
  isFromCanada?: boolean
): Partial<FormType> => {
  const initialValues: Partial<FormType> = {
    ...resident,
    dnacprReviewDate: resident?.dnacprReviewDate
      ? dayjs(resident?.dnacprReviewDate)
      : null,
    admissionDate: resident?.admissionDate
      ? dayjs(resident.admissionDate)
      : undefined,
    dolsApplicationDate: resident?.dolsApplicationDate
      ? dayjs(resident.dolsApplicationDate)
      : undefined,
    dolsReviewDate: resident?.dolsReviewDate
      ? dayjs(resident.dolsReviewDate)
      : undefined,
    dolsStartDate: resident?.dolsStartDate
      ? dayjs(resident.dolsStartDate)
      : undefined,
    dolsEndDate: resident?.dolsEndDate
      ? dayjs(resident.dolsEndDate)
      : undefined,
    nhsNumber: resident?.nhsNumber
      ? getNHSFormattedNumber(resident?.nhsNumber, isFromCanada)
      : undefined,
    mhaStatus: resident?.mhaStatus ?? false
  }

  return initialValues
}

export default ResidentCareInformation
